import { 
  Dumbbell, Heart, Apple, Flower2, Flame, Building2, 
  Sparkles, Music, Sword, LucideIcon 
} from "lucide-react";
import { ProviderType } from "@/types/booking";

export interface BookingTheme {
  gradient: string;           // Gradiente principal para botones y headers
  gradientLight: string;      // Gradiente suave para fondos
  gradientHover: string;      // Gradiente para estados hover
  accentColor: string;        // Color de acento HSL
  icon: LucideIcon;           // Icono principal
  emoji: string;              // Emoji representativo
  motivationalText: string;   // Texto motivador para el header
  confirmationText: string;   // Texto de confirmación
  bgPattern: string;          // Patrón de fondo opcional
}

export const BOOKING_THEMES: Record<ProviderType, BookingTheme> = {
  trainer: {
    gradient: "from-blue-600 via-blue-500 to-indigo-600",
    gradientLight: "from-blue-50 to-indigo-50",
    gradientHover: "from-blue-700 via-blue-600 to-indigo-700",
    accentColor: "217 91% 60%",
    icon: Dumbbell,
    emoji: "🏋️",
    motivationalText: "Tu transformación empieza aquí",
    confirmationText: "¡A entrenar se ha dicho!",
    bgPattern: "radial-gradient(circle at 20% 80%, hsl(217 91% 60% / 0.1) 0%, transparent 50%)",
  },
  physiotherapist: {
    gradient: "from-sky-500 via-cyan-500 to-teal-500",
    gradientLight: "from-sky-50 to-teal-50",
    gradientHover: "from-sky-600 via-cyan-600 to-teal-600",
    accentColor: "199 89% 48%",
    icon: Heart,
    emoji: "💆",
    motivationalText: "Recupera tu bienestar",
    confirmationText: "¡Tu cuerpo te lo agradecerá!",
    bgPattern: "radial-gradient(circle at 80% 20%, hsl(199 89% 48% / 0.1) 0%, transparent 50%)",
  },
  nutritionist: {
    gradient: "from-emerald-500 via-green-500 to-lime-500",
    gradientLight: "from-emerald-50 to-lime-50",
    gradientHover: "from-emerald-600 via-green-600 to-lime-600",
    accentColor: "142 71% 45%",
    icon: Apple,
    emoji: "🥗",
    motivationalText: "Nutre tu mejor versión",
    confirmationText: "¡Hacia una vida más saludable!",
    bgPattern: "radial-gradient(circle at 30% 70%, hsl(142 71% 45% / 0.1) 0%, transparent 50%)",
  },
  yoga: {
    gradient: "from-purple-600 via-violet-500 to-pink-500",
    gradientLight: "from-purple-50 to-pink-50",
    gradientHover: "from-purple-700 via-violet-600 to-pink-600",
    accentColor: "270 95% 75%",
    icon: Flower2,
    emoji: "🧘",
    motivationalText: "Encuentra tu paz interior",
    confirmationText: "¡Namaste!",
    bgPattern: "radial-gradient(circle at 70% 30%, hsl(270 95% 75% / 0.1) 0%, transparent 50%)",
  },
  crossfit: {
    gradient: "from-orange-500 via-amber-500 to-yellow-500",
    gradientLight: "from-orange-50 to-yellow-50",
    gradientHover: "from-orange-600 via-amber-600 to-yellow-600",
    accentColor: "25 95% 53%",
    icon: Flame,
    emoji: "💪",
    motivationalText: "Supera tus límites",
    confirmationText: "¡Nos vemos en el box!",
    bgPattern: "radial-gradient(circle at 50% 50%, hsl(25 95% 53% / 0.1) 0%, transparent 50%)",
  },
  gym: {
    gradient: "from-green-600 via-emerald-500 to-teal-500",
    gradientLight: "from-green-50 to-teal-50",
    gradientHover: "from-green-700 via-emerald-600 to-teal-600",
    accentColor: "160 84% 39%",
    icon: Building2,
    emoji: "🏢",
    motivationalText: "Tu gimnasio, tu espacio",
    confirmationText: "¡Te esperamos!",
    bgPattern: "radial-gradient(circle at 20% 20%, hsl(160 84% 39% / 0.1) 0%, transparent 50%)",
  },
  pilates: {
    gradient: "from-teal-500 via-cyan-500 to-sky-500",
    gradientLight: "from-teal-50 to-sky-50",
    gradientHover: "from-teal-600 via-cyan-600 to-sky-600",
    accentColor: "174 72% 56%",
    icon: Sparkles,
    emoji: "🤸",
    motivationalText: "Fortalece cuerpo y mente",
    confirmationText: "¡A trabajar el core!",
    bgPattern: "radial-gradient(circle at 60% 80%, hsl(174 72% 56% / 0.1) 0%, transparent 50%)",
  },
  dance: {
    gradient: "from-pink-500 via-rose-500 to-red-500",
    gradientLight: "from-pink-50 to-rose-50",
    gradientHover: "from-pink-600 via-rose-600 to-red-600",
    accentColor: "330 81% 60%",
    icon: Music,
    emoji: "💃",
    motivationalText: "Expresa con movimiento",
    confirmationText: "¡A bailar!",
    bgPattern: "radial-gradient(circle at 40% 60%, hsl(330 81% 60% / 0.1) 0%, transparent 50%)",
  },
  martial_arts: {
    gradient: "from-red-600 via-rose-600 to-orange-500",
    gradientLight: "from-red-50 to-orange-50",
    gradientHover: "from-red-700 via-rose-700 to-orange-600",
    accentColor: "0 72% 51%",
    icon: Sword,
    emoji: "🥋",
    motivationalText: "Disciplina y fortaleza",
    confirmationText: "¡Oss!",
    bgPattern: "radial-gradient(circle at 80% 80%, hsl(0 72% 51% / 0.1) 0%, transparent 50%)",
  },
};

export const getBookingTheme = (providerType: ProviderType): BookingTheme => {
  return BOOKING_THEMES[providerType];
};

// Helper to get step icons by step name
export const STEP_ICONS = {
  service: "🎯",
  location: "📍", 
  datetime: "📅",
  details: "✏️",
  payment: "💳",
  confirmation: "✅",
} as const;
